import { getDiceServiceUrl, getMediaUrl, isDebug } from "@/constants";
import { customSetManifestFileName } from "@/constants/diceConfig";
import { getManifestData } from "@/helpers/getManifestData";
import { preloadAssets } from "@/helpers/preloadAssets";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useDiceSets } from "@/hooks/useDiceSets";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import type { Manifest } from "@/types";
import type { User } from "@dndbeyond/auth/types/user";
import { useEffect, useState } from "react";

export const useManifests = (
  nodeEnv?: string,
  env?: string,
  gameId?: string,
  stt?: string,
  user?: User,
  preview?: boolean,
) => {
  const [manifests, setManifests] = useState<Record<string, Manifest>>();
  const diceServiceUrl = getDiceServiceUrl(String(nodeEnv), String(env));
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);
  const selectedSet = useDicePreviewerStore((state) => state.selectedSet);
  const mediaUrl = getMediaUrl(String(nodeEnv), String(env));
  const diceSets = useDiceSets(nodeEnv, env);
  const userSetId = useUserDiceDataStore(
    (state) => state.getUserDiceData(Number(user?.id))?.setId,
  );

  useEffect(() => {
    if (!(nodeEnv && env && diceSets)) return;

    const setId = preview ? selectedSet?.id : userSetId;

    (async () => {
      const manifestUrl =
        customUrlMap?.[customSetManifestFileName] ||
        diceSets.find(({ id }) => id === setId)?.manifestUrl;

      if (!manifestUrl) {
        if (isDebug) {
          console.warn(
            `[DiceCanvases] Manifest URL for set ID "${setId}" not found.`,
          );
        }

        return;
      }

      const manifestData = await getManifestData({
        mediaUrl,
        setId: String(setId),
        manifestUrl,
        customUrlMap,
      });

      if (!manifestData) return;

      setManifests((prev) => ({
        ...prev,
        [String(setId)]: manifestData,
      }));
      preloadAssets(mediaUrl, [manifestData], customUrlMap);
    })();
  }, [
    customUrlMap,
    diceSets,
    env,
    mediaUrl,
    nodeEnv,
    preview,
    selectedSet?.id,
    userSetId,
  ]);

  useEffect(() => {
    if (!(nodeEnv && env && diceSets && gameId && stt)) return;

    (async () => {
      const data: Record<string, Manifest> = {};
      const setIds: string[] = [];

      try {
        const { data } = await (
          await fetch(
            `${diceServiceUrl}/diceuserconfig/v1/getsetidsincampaign/${gameId}`,
            {
              headers: {
                Authorization: `Bearer ${stt}`,
              },
            },
          )
        ).json();

        setIds.push(...data);
      } catch (error) {
        if (isDebug) {
          console.error(
            `[DiceCanvases] Error fetching set IDs for game ID "${gameId}":`,
            error,
          );
        }
      }

      await Promise.all(
        setIds
          .filter((sId) => sId !== userSetId)
          .map(async (setId) => {
            const manifestUrl =
              customUrlMap?.[customSetManifestFileName] ||
              diceSets.find(({ id }) => id === setId)?.manifestUrl;

            if (!manifestUrl) {
              if (isDebug) {
                console.warn(
                  `[DiceCanvases] Manifest URL for set ID "${setId}" not found.`,
                );
              }

              return;
            }

            const manifestData = await getManifestData({
              mediaUrl,
              setId,
              manifestUrl,
              customUrlMap,
            });

            if (!manifestData) return;

            data[setId] = manifestData;
          }),
      );

      setManifests((prev) => ({
        ...prev,
        ...data,
      }));
      preloadAssets(mediaUrl, Object.values(data), customUrlMap);

      if (isDebug) {
        console.log("[DiceCanvases] Preloading assets complete.");
      }
    })();
  }, [
    customUrlMap,
    diceServiceUrl,
    diceSets,
    env,
    gameId,
    mediaUrl,
    nodeEnv,
    stt,
    userSetId,
  ]);

  return manifests;
};
